import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    useTheme,
    CircularProgress
} from '@mui/material';

interface StatusItem {
    id: string | number;
    status: string;
    time: string;
    description: string;
    timestamp: Date;
}

interface StatusTimelineProps {
    initialItems?: StatusItem[];
    loading?: boolean;
    onLoadMore?: () => void;
    maxHeight?: string | number;
}

const StatusTimelineItem: React.FC<{ item: StatusItem; isLast: boolean }> = ({ item, isLast }) => {
    return (
        <Box sx={{ display: 'flex', position: 'relative' }}>
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: '#1A1A1A',
                    zIndex: 1,
                    flexShrink: 0,
                }}
            />

            {!isLast && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 24,
                        top: 48,
                        bottom: -16,
                        width: 2,
                        backgroundColor: '#1A1A1A',
                        zIndex: 0,
                    }}
                />
            )}

            <Box sx={{ ml: 2, pb: 4 }}>
                <Typography variant="subtitle1" fontWeight="600">
                    {item.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {item.time} - {item.description}
                </Typography>
            </Box>
        </Box>
    );
};

const StatusTimeline: React.FC<StatusTimelineProps> = ({
    initialItems = [],
    loading = false,
    onLoadMore,
    maxHeight = 400
}) => {
    const [statusItems, setStatusItems] = useState<StatusItem[]>(initialItems);
    const containerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const handleScroll = () => {
        if (!containerRef.current || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

        if (isNearBottom && onLoadMore) {
            onLoadMore();
        }
    };

    useEffect(() => {
        if (initialItems.length > 0) {
            setStatusItems(initialItems);
        }
    }, [initialItems]);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Typography variant="h5" component="h2" fontWeight="500" mb={1}>
                Estatus
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={3}>
                Aquí aparecen los estatus hechos por el conductor en el día
            </Typography>

            <Box
                ref={containerRef}
                sx={{
                    overflowY: 'auto',
                    maxHeight,
                    pr: 2,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#a8a8a8',
                        },
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 #f1f1f1',
                }}
                onScroll={handleScroll}
            >
                {statusItems.length > 0 ? (
                    <Box sx={{ pl: 1, pt: 1 }}>
                        {statusItems.map((item, index) => (
                            <StatusTimelineItem
                                key={item.id}
                                item={item}
                                isLast={index === statusItems.length - 1}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No hay estatus registrados hoy
                        </Typography>
                    </Box>
                )}

                {loading && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default StatusTimeline;